import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Empty,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  AudioOutlined,
  CodeOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileZipOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  HomeOutlined,
  ReloadOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";

import config from "../../api_config.json";
import { useAppContext } from "../../context";
import { useAuth0 } from "../../react-auth0-spa";
import { createInternalDriveApi } from "./api";
import InternalDriveUploader from "./InternalDriveUploader";
import {
  buildDestinationKey,
  buildMissionControlLink,
  formatFileSize,
  formatModifiedAt,
} from "./utils";

const { Text, Title } = Typography;

const itemIcons = {
  folder: <FolderOutlined style={{ color: "#1677ff" }} />,
  image: <FileImageOutlined style={{ color: "#52c41a" }} />,
  video: <VideoCameraOutlined style={{ color: "#722ed1" }} />,
  pdf: <FilePdfOutlined style={{ color: "#f5222d" }} />,
  document: <FileTextOutlined style={{ color: "#fa8c16" }} />,
  code: <CodeOutlined style={{ color: "#13c2c2" }} />,
  archive: <FileZipOutlined style={{ color: "#faad14" }} />,
  audio: <AudioOutlined style={{ color: "#eb2f96" }} />,
  file: <FileOutlined />,
};

const InternalDrive = () => {
  const history = useHistory();
  const location = useLocation();
  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();
  const search = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const requestedEnvironment = search.get("env");
  const environment = ["dev", "prod"].includes(requestedEnvironment)
    ? requestedEnvironment
    : isProd
      ? "prod"
      : "dev";
  const api = useMemo(
    () =>
      createInternalDriveApi({
        baseUrl: config.baseUrl[environment],
        getTokenSilently,
      }),
    [environment, getTokenSilently],
  );

  const currentPath = search.get("path") || "";
  const requestedOpenKey = search.get("open");
  const requestedMode = search.get("mode") || "view";

  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [continuationToken, setContinuationToken] = useState(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [moving, setMoving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchItems = useCallback(
    async ({ append = false, token } = {}) => {
      append ? setLoadingMore(true) : setLoading(true);
      try {
        const data = await api.listItems(currentPath, token);
        setItems((current) =>
          append ? [...current, ...(data.items || [])] : data.items || [],
        );
        setBreadcrumbs(data.breadcrumbs || []);
        setContinuationToken(data.nextContinuationToken || null);
        setIsTruncated(Boolean(data.isTruncated));
      } catch (error) {
        message.error(error.message || "Impossibile caricare Internal Drive");
        if (!append) {
          setItems([]);
        }
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [api, currentPath],
  );

  useEffect(() => {
    if (!requestedOpenKey) {
      fetchItems();
    }
  }, [fetchItems, requestedOpenKey]);

  useEffect(() => {
    if (!requestedOpenKey) {
      return undefined;
    }

    let cancelled = false;
    const openRequestedItem = async () => {
      try {
        const access = await api.getAccess(requestedOpenKey, requestedMode);
        if (!cancelled) {
          window.location.replace(access.sessionUrl);
        }
      } catch (error) {
        if (!cancelled) {
          message.error(error.message || "Impossibile aprire il contenuto");
          const params = new URLSearchParams();
          const parentPath = requestedOpenKey.includes("/")
            ? `${requestedOpenKey.split("/").slice(0, -1).join("/")}/`
            : "";
          if (parentPath) {
            params.set("path", parentPath);
          }
          if (requestedEnvironment) {
            params.set("env", requestedEnvironment);
          }
          history.replace({
            pathname: "/internal-drive",
            search: params.toString() ? `?${params}` : "",
          });
        }
      }
    };
    openRequestedItem();
    return () => {
      cancelled = true;
    };
  }, [api, history, requestedEnvironment, requestedMode, requestedOpenKey]);

  const navigateToFolder = (path) => {
    const params = new URLSearchParams();
    if (path) {
      params.set("path", path);
    }
    params.set("env", environment);
    history.push({
      pathname: "/internal-drive",
      search: params.toString() ? `?${params}` : "",
    });
  };

  const openItem = async (item, mode = "view") => {
    const targetWindow = window.open("about:blank", "_blank");
    try {
      const access = await api.getAccess(item.path, mode);
      if (targetWindow) {
        targetWindow.location.replace(access.sessionUrl);
      } else {
        window.location.assign(access.sessionUrl);
      }
    } catch (error) {
      targetWindow?.close();
      message.error(error.message || "Impossibile aprire il contenuto");
    }
  };

  const copyAuthenticatedLink = async (item) => {
    try {
      await navigator.clipboard.writeText(
        buildMissionControlLink({
          key: item.path,
          environment,
        }),
      );
      message.success("Link Mission Control copiato");
    } catch (error) {
      message.error("Impossibile copiare il link");
    }
  };

  const createFolder = async () => {
    const name = folderName.trim();
    if (!name) {
      message.error("Inserisci il nome della cartella");
      return;
    }
    setCreatingFolder(true);
    try {
      await api.createFolder(currentPath, name);
      setFolderName("");
      setFolderModalOpen(false);
      setCreatingFolder(false);
      message.success("Cartella creata");
      fetchItems();
    } catch (error) {
      setCreatingFolder(false);
      message.error(error.message || "Impossibile creare la cartella");
    }
  };

  const showRename = (item) => {
    setRenameTarget(item);
    setRenameValue(item.name);
  };

  const renameItem = async () => {
    const name = renameValue.trim();
    if (!renameTarget || !name) {
      message.error("Inserisci un nome valido");
      return;
    }
    setMoving(true);
    try {
      await api.moveItem({
        sourceKey: renameTarget.path,
        destinationKey: buildDestinationKey({
          currentPath,
          name,
          type: renameTarget.type,
        }),
        type: renameTarget.type,
      });
      setRenameTarget(null);
      setRenameValue("");
      message.success("Elemento rinominato");
      fetchItems();
    } catch (error) {
      message.error(error.message || "Impossibile rinominare l'elemento");
    } finally {
      setMoving(false);
    }
  };

  const deleteItem = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    try {
      await api.deleteItem(deleteTarget.path, deleteTarget.type);
      setDeleteTarget(null);
      setDeleteConfirmation("");
      message.success(
        deleteTarget.type === "folder"
          ? "Cartella eliminata"
          : "File eliminato",
      );
      fetchItems();
    } catch (error) {
      message.error(error.message || "Impossibile eliminare l'elemento");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      render: (name, item) => (
        <Space>
          <span style={{ fontSize: 22 }}>
            {itemIcons[item.type] || itemIcons.file}
          </span>
          {item.type === "folder" ? (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => navigateToFolder(item.path)}
            >
              {name}
            </Button>
          ) : (
            <Text>{name}</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Dimensione",
      dataIndex: "size",
      key: "size",
      width: 120,
      render: (size, item) =>
        item.type === "folder" ? "—" : formatFileSize(size),
    },
    {
      title: "Modificato",
      dataIndex: "lastModified",
      key: "lastModified",
      width: 180,
      render: formatModifiedAt,
    },
    {
      title: "Azioni",
      key: "actions",
      width: 220,
      render: (_, item) => (
        <Space size={2}>
          {item.type === "folder" ? (
            <Tooltip title="Apri cartella">
              <Button
                type="text"
                icon={<FolderOpenOutlined />}
                onClick={() => navigateToFolder(item.path)}
              />
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Apri">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => openItem(item)}
                />
              </Tooltip>
              <Tooltip title="Scarica">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => openItem(item, "download")}
                />
              </Tooltip>
              <Tooltip title="Copia link con accesso Mission Control">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => copyAuthenticatedLink(item)}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Rinomina">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showRename(item)}
            />
          </Tooltip>
          <Tooltip title="Elimina">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setDeleteTarget(item);
                setDeleteConfirmation("");
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (requestedOpenKey) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Apertura del contenuto protetto…</p>
      </div>
    );
  }

  return (
    <div style={{ margin: 16 }}>
      <Space
        direction="vertical"
        size="large"
        style={{ display: "flex", width: "100%" }}
      >
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Internal Drive
          </Title>
          <Text type="secondary">
            File, report e mini-siti riservati agli utenti di Mission Control.
          </Text>
        </div>

        <Breadcrumb
          items={[
            {
              title: (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a onClick={() => navigateToFolder("")}>
                  <HomeOutlined /> Internal Drive
                </a>
              ),
            },
            ...breadcrumbs.map((crumb) => ({
              title: (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a onClick={() => navigateToFolder(crumb.path)}>{crumb.name}</a>
              ),
            })),
          ]}
        />

        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            justifyContent: "space-between",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            {breadcrumbs[breadcrumbs.length - 1]?.name || "Tutti i contenuti"}
          </Title>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => fetchItems()}>
              Aggiorna
            </Button>
            <Button
              icon={<FolderAddOutlined />}
              onClick={() => {
                setFolderName("");
                setFolderModalOpen(true);
              }}
            >
              Nuova cartella
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setShowUploader((visible) => !visible)}
            >
              Carica
            </Button>
          </Space>
        </div>

        {showUploader && (
          <InternalDriveUploader
            api={api}
            path={currentPath}
            onComplete={() => fetchItems()}
          />
        )}

        {loading ? (
          <div style={{ padding: 64, textAlign: "center" }}>
            <Spin size="large" />
          </div>
        ) : items.length ? (
          <>
            <Table
              dataSource={items}
              columns={columns}
              rowKey="path"
              pagination={false}
              scroll={{ x: 760 }}
            />
            {isTruncated && (
              <div style={{ textAlign: "center" }}>
                <Button
                  loading={loadingMore}
                  onClick={() =>
                    fetchItems({
                      append: true,
                      token: continuationToken,
                    })
                  }
                >
                  Carica altri
                </Button>
              </div>
            )}
          </>
        ) : (
          <Empty description="Questa cartella è vuota" />
        )}
      </Space>

      <Modal
        open={folderModalOpen}
        title="Nuova cartella"
        okText="Crea"
        cancelText="Annulla"
        confirmLoading={creatingFolder}
        onCancel={() => {
          setFolderModalOpen(false);
          setFolderName("");
        }}
        onOk={createFolder}
      >
        <Input
          autoFocus
          placeholder="Nome cartella"
          value={folderName}
          onChange={(event) => setFolderName(event.target.value)}
          onPressEnter={createFolder}
        />
      </Modal>

      <Modal
        open={Boolean(renameTarget)}
        title={`Rinomina ${renameTarget?.type === "folder" ? "cartella" : "file"}`}
        okText="Rinomina"
        cancelText="Annulla"
        confirmLoading={moving}
        onCancel={() => {
          setRenameTarget(null);
          setRenameValue("");
        }}
        onOk={renameItem}
      >
        <Input
          autoFocus
          value={renameValue}
          onChange={(event) => setRenameValue(event.target.value)}
          onPressEnter={renameItem}
        />
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        title={`Elimina ${deleteTarget?.type === "folder" ? "cartella" : "file"}`}
        okText="Elimina"
        okType="danger"
        cancelText="Annulla"
        confirmLoading={deleting}
        okButtonProps={{
          disabled: deleteConfirmation !== deleteTarget?.name,
        }}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteConfirmation("");
        }}
        onOk={deleteItem}
      >
        <p>
          {deleteTarget?.type === "folder"
            ? "La cartella e tutto il suo contenuto saranno rimossi."
            : "Il file sarà rimosso."}{" "}
          Digita il nome per confermare:
        </p>
        <Text strong copyable>
          {deleteTarget?.name}
        </Text>
        <Input
          style={{ marginTop: 12 }}
          value={deleteConfirmation}
          onChange={(event) => setDeleteConfirmation(event.target.value)}
        />
      </Modal>
    </div>
  );
};

export default InternalDrive;
